import React from "react";
import { useQuery } from "@tanstack/react-query";
import { UserCircle } from "lucide-react";
import { TEAM_INFO } from "../lib/teamMap";
import { useAuth } from "../lib/AuthContext";
import { api } from "../lib/api";
import { TeamBadge } from "./TeamBadge";

const OWNER_C = "#FB923C";
const ACC_C = "#60A5FA";
const fmtDate = (iso) => { const d = iso ? new Date(iso) : null; return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—"; };
const fmtSecs = (x) => { x = Math.round(x || 0); if (x < 60) return `${x}s`; const m = Math.floor(x / 60); return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`; };

// Account tile on: your account info fills the team switchboard's exact box.
export function AccountNavPanel() {
    const { user } = useAuth();
    const { data: account } = useQuery({ queryKey: ["account"], queryFn: () => api.get("/account").then((r) => r.data), enabled: !!user });
    const { data: pageStats = [] } = useQuery({ queryKey: ["my-page-stats"], queryFn: () => api.get("/account/page-stats").then((r) => r.data), enabled: !!user });
    const p = account?.profile || {};
    const team = p.main_team_id ? TEAM_INFO[p.main_team_id] : null;
    const c = team?.color || ACC_C;
    const info = [
        ["Member Since", fmtDate(p.created_at)],
        ["Favourite Team", team?.abbr || "—"],
        ["Pages Viewed", pageStats.length],
        ["Time Tracked", fmtSecs(pageStats.reduce((a, x) => a + (x.seconds || 0), 0))],
    ];
    return (
        <div className="relative h-full w-full flex flex-col justify-between gap-1.5 p-2 rounded-xl border bg-[#0A0B10]/90 backdrop-blur overflow-hidden" style={{ borderColor: `${c}66` }} data-testid="account-nav-panel">
            <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(120% 140% at 0% 0%, ${c}33 0%, transparent 65%)` }} />
            <div className="relative flex items-center gap-2 min-w-0">
                <span className="h-8 w-8 shrink-0 rounded-full bg-black border border-white/15 flex items-center justify-center overflow-hidden">
                    {p.main_team_id ? <span style={{ transform: "scale(0.6)" }}><TeamBadge teamId={p.main_team_id} size="lg" asLink={false} /></span> : <UserCircle className="h-5 w-5 text-white/40" />}
                </span>
                <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-extrabold text-white truncate leading-tight" data-testid="account-nav-name">
                        {p.name || (user?.email || "").split("@")[0]}{p.role && p.role !== "user" && <span className="text-white/45 font-bold"> · {p.role[0].toUpperCase() + p.role.slice(1)}</span>}
                    </div>
                    <div className="text-[10px] text-white/55 truncate" data-testid="account-nav-email">{p.email || user?.email}</div>
                </div>
            </div>
            <div className="relative grid grid-cols-2 gap-1" data-testid="account-nav-info">
                {info.map(([l, v]) => (
                    <div key={l} className="min-w-0 rounded-lg border border-white/10 bg-black/35 px-2 py-1" data-testid={`account-nav-${l.toLowerCase().replace(/\s+/g, "-")}`}>
                        <div className="text-[12px] font-black text-white leading-tight whitespace-nowrap">{v}</div>
                        <div className="text-[8px] uppercase tracking-wider text-white/50 font-bold whitespace-nowrap">{l}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export const OWNER_TOOLS_COLOR = OWNER_C;
export const ACCOUNT_COLOR = ACC_C;
